import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, DatePicker, Tabs } from 'antd';
import moment from 'moment';

import { fetchLocations, fetchLocationVendorData, setSearchLocations } from './offices-actions';
import { officesTabsConfig } from './offices-tabs-config';
import { getIsLoading, getLocationsTotal, getSearch } from './offices-selectors';
import { getReadTabsPermissions, getWriteTabsPermissions, getPermittedMenuTabs } from '../../store/user/user-reducer';
import { fetchVendors } from '../vendors/vendors-actions';

import BaseGrid from '../grid/BaseGrid';
import FutureScheduleGrid from '../grid/FutureScheduleGrid';
import SearchLocations from '../header/search/SearchLocations';
import MapWrapper from '../map/MapWrapper';

const { TabPane } = Tabs;

function Offices() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('');
  const [scheduleSubtab, setScheduleSubtab] = useState('current');
  const [scheduleDate, setScheduleDate] = useState(moment());
  const [coreScheduleDate, setCoreScheduleDate] = useState(() => moment().startOf('day').day(0));
  const [isAllSelected, setIsAllSelected] = useState(false);
  const futureWeekStart = moment().startOf('isoWeek').add(1, 'week');
  const currentWeekStart = scheduleDate.clone().startOf('isoWeek');
  const currentWeekEnd = currentWeekStart.clone().add(6, 'days');
  const coreWeekStart = coreScheduleDate.clone().startOf('day').day(0);
  const coreWeekEnd = coreWeekStart.clone().add(6, 'days');

  const total = useSelector(getLocationsTotal);
  const search = useSelector(getSearch);
  const loading = useSelector(getIsLoading);
  const isReadAllowed = useSelector(getReadTabsPermissions);
  const isWriteAllowed = useSelector(getWriteTabsPermissions);
  const permittedMenuTabs = useSelector(getPermittedMenuTabs);

  useEffect(() => {
    if (search.activeTab === 'schedule' && search.scheduleSubtab === 'future') {
      return;
    }

    dispatch(fetchLocations(search));
    // Also fetch vendor data when search changes and MVC tab is active
    if (search.activeTab === 'mvc') {
      dispatch(fetchLocationVendorData(search));
    }
  }, [search]);

  useEffect(() => {
    setActiveTab(defaultActiveKey());
  }, []);

  useEffect(() => {
    if (activeTab !== 'map') {
      const isScheduleTab = activeTab === 'schedule';
      const selectedDate = moment();

      if (isScheduleTab) {
        const coreWeekStart = selectedDate.clone().startOf('day').day(0);
        setScheduleSubtab('current');
        setScheduleDate(selectedDate);
        setCoreScheduleDate(coreWeekStart);
      }

      dispatch(
        setSearchLocations({
          activeTab,
          effectiveDate: isScheduleTab ? selectedDate.format('YYYY-MM-DD') : null,
          ...(isScheduleTab && { scheduleSubtab: 'current' }),
        })
      );
    }
    // When MVC tab is selected, fetch vendor data for locations and vendors list
    if (activeTab === 'mvc') {
      dispatch(fetchLocationVendorData(search));
      dispatch(fetchVendors());
    }
  }, [activeTab]);

  const defaultActiveKey = () => {
    const tabKeys = Object.keys(isReadAllowed)
      .map((key) => [key, isReadAllowed[key] || isWriteAllowed[key]])
      .filter(([_, value]) => value)
      .map(([key, _]) => key);

    return tabKeys[0];
  };

  const onPageChange = (searchData) => {
    dispatch(setSearchLocations(searchData));
  };

  return (
    <>
      <SearchLocations toggleIsAllSelected={(val) => setIsAllSelected(val)} isColored={activeTab === 'map'} />
      <Tabs
        className={'common-tabs'}
        activeKey={activeTab}
        onChange={(activeKey) => setActiveTab(activeKey)}
        destroyInactiveTabPane
      >
        {Object.entries(officesTabsConfig(null))
          // only include tabs the user can read/write OR that are explicitly permitted via permittedMenuTabs
          .filter(([key, tab]) => {
            if (isReadAllowed[key] || isWriteAllowed[key]) return true;
            const tabTitle = (tab?.title || '').toLowerCase();
            // permittedMenuTabs entries are stored as `Menu|Tab`.
            return (permittedMenuTabs || []).some((pm: string) => {
              const parts = (pm || '').split('|').map((s) => s.trim().toLowerCase());
              return parts[1] === tabTitle;
            });
          })
          .map(([key, tab]) => {
            return (
              <TabPane tab={(tab.displayTitle || tab.title).toUpperCase()} key={key}>
                <TabContainer>
                  {tab.title === 'map' ? (
                    <MapWrapper />
                  ) : key === 'schedule' ? (
                    <Tabs
                      className='schedule-subtabs'
                      activeKey={scheduleSubtab}
                      onChange={(subtab) => {
                        setScheduleSubtab(subtab);
                        const effectiveDate =
                          subtab === 'current' ? scheduleDate : subtab === 'core' ? coreWeekStart : futureWeekStart;
                        dispatch(
                          setSearchLocations({
                            effectiveDate: effectiveDate.format('YYYY-MM-DD'),
                            scheduleSubtab: subtab,
                          })
                        );
                      }}
                      destroyInactiveTabPane
                    >
                      <TabPane tab='CORE' key='core'>
                        <ScheduleToolbar>
                          <ScheduleControls>
                            <DatePicker
                              aria-label='Core schedule week'
                              value={coreScheduleDate}
                              picker='week'
                              format='YYYY-[W]ww'
                              allowClear={false}
                              inputReadOnly
                              disabledDate={(date) =>
                                date &&
                                date.clone().startOf('day').day(0).isBefore(moment().startOf('day').day(0), 'day')
                              }
                              onChange={(date) => {
                                const selectedWeek = (date || moment()).clone().startOf('day').day(0);
                                setCoreScheduleDate(selectedWeek);
                                dispatch(
                                  setSearchLocations({
                                    effectiveDate: selectedWeek.format('YYYY-MM-DD'),
                                    scheduleSubtab: 'core',
                                  })
                                );
                              }}
                            />
                            <Button
                              disabled={coreWeekStart.isSame(moment().startOf('day').day(0), 'day')}
                              onClick={() => {
                                const thisWeek = moment().startOf('day').day(0);
                                setCoreScheduleDate(thisWeek);
                                dispatch(
                                  setSearchLocations({
                                    effectiveDate: thisWeek.format('YYYY-MM-DD'),
                                    scheduleSubtab: 'core',
                                  })
                                );
                              }}
                            >
                              Current week
                            </Button>
                          </ScheduleControls>
                          <ScheduleWeekLabel>
                            <span>WEEK OF</span>
                            <strong>
                              {coreWeekStart.format('MMM D')} - {coreWeekEnd.format('MMM D, YYYY')}
                            </strong>
                          </ScheduleWeekLabel>
                        </ScheduleToolbar>
                        <BaseGrid
                          id={key}
                          activeTab={activeTab}
                          total={total}
                          isAllSelected={isAllSelected}
                          toggleIsAllSelected={(val) => setIsAllSelected(val)}
                          search={search}
                          setSearch={onPageChange}
                          loading={loading}
                          scheduleSubtab='core'
                          scheduleDate={coreScheduleDate}
                        />
                      </TabPane>
                      <TabPane tab='CURRENT' key='current'>
                        <ScheduleToolbar>
                          <ScheduleControls>
                            <DatePicker
                              aria-label='Current schedule week date'
                              value={scheduleDate}
                              format='YYYY-MM-DD'
                              allowClear={false}
                              inputReadOnly
                              disabledDate={(date) => date && date.isBefore(moment(), 'day')}
                              onChange={(date) => {
                                const selectedDate = date || moment();
                                setScheduleDate(selectedDate);
                                dispatch(
                                  setSearchLocations({
                                    effectiveDate: selectedDate.format('YYYY-MM-DD'),
                                    scheduleSubtab: 'current',
                                  })
                                );
                              }}
                            />
                            <Button
                              disabled={scheduleDate.isSame(moment(), 'day')}
                              onClick={() => {
                                const today = moment();
                                setScheduleDate(today);
                                dispatch(
                                  setSearchLocations({
                                    effectiveDate: today.format('YYYY-MM-DD'),
                                    scheduleSubtab: 'current',
                                  })
                                );
                              }}
                            >
                              Today
                            </Button>
                          </ScheduleControls>
                          <ScheduleWeekLabel>
                            <span>WEEK OF</span>
                            <strong>
                              {currentWeekStart.format('MMM D')} - {currentWeekEnd.format('MMM D, YYYY')}
                            </strong>
                          </ScheduleWeekLabel>
                        </ScheduleToolbar>
                        <BaseGrid
                          id={key}
                          activeTab={activeTab}
                          total={total}
                          isAllSelected={isAllSelected}
                          toggleIsAllSelected={(val) => setIsAllSelected(val)}
                          search={search}
                          setSearch={onPageChange}
                          loading={loading}
                          scheduleSubtab='current'
                          scheduleDate={scheduleDate}
                        />
                      </TabPane>
                      <TabPane tab='UPCOMING' key='future'>
                        <FutureScheduleGrid search={search} setSearch={onPageChange} />
                      </TabPane>
                    </Tabs>
                  ) : (
                    <BaseGrid
                      id={key}
                      activeTab={activeTab}
                      total={total}
                      isAllSelected={isAllSelected}
                      toggleIsAllSelected={(val) => setIsAllSelected(val)}
                      search={search}
                      setSearch={onPageChange}
                      loading={loading}
                    />
                  )}
                </TabContainer>
              </TabPane>
            );
          })}
      </Tabs>
    </>
  );
}

export default Offices;

const TabContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: space-between;
`;

const ScheduleToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 8px 16px 12px;
  padding: 10px 12px;
  background: #f4f8fb;
  border: 1px solid #dfdfdf;
  border-radius: 4px;

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }
`;

const ScheduleControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .ant-picker {
    width: 160px;
    border-radius: 4px;
  }

  .ant-btn {
    height: 32px;
    border-radius: 4px;
    color: #40668c;
    border-color: #b9cbd8;
  }
`;

const ScheduleWeekLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #545454;
  text-align: right;

  span {
    color: #757575;
    font-size: 10px;
    font-weight: 700;
  }

  strong {
    color: #003366;
    font-size: 14px;
    font-weight: 600;
  }

  @media (max-width: 560px) {
    text-align: left;
  }
`;
